import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import ScheduleViewerClock, {
  ScheduleNowLine,
  useScheduleNowTick,
} from '../components/ScheduleViewerClock';
import {
  WEEKDAY_NAMES,
  addDaysISO,
  formatDateShort,
  formatDualTime,
  formatDualTimeFromTemplate,
  loadViewerTzPreference,
  minutesFromMidnight,
  mondayOf,
  moscowDateISO,
  moscowWeekday,
  nowLineTopPx,
  occurrenceDateMoscow,
  scheduleGridExtent,
  timeFromOccurrenceStart,
  weekDates,
} from '../utils/scheduleDates';

const HOUR_HEIGHT = 40;

function assignLanes(items) {
  const sorted = [...items].sort(
    (a, b) => a.startMin - b.startMin || a.endMin - b.endMin || String(a.id).localeCompare(String(b.id))
  );
  const laneEnds = [];
  const withLane = sorted.map((item) => {
    let lane = 0;
    while (lane < laneEnds.length && laneEnds[lane] > item.startMin) lane += 1;
    if (lane === laneEnds.length) laneEnds.push(0);
    laneEnds[lane] = item.endMin;
    return { ...item, lane };
  });
  const laneCount = Math.max(1, ...withLane.map((x) => x.lane + 1));
  return withLane.map((x) => ({ ...x, laneCount }));
}

function laneCellStyle(startTime, durationMinutes, gridStartMin, lane, laneCount) {
  const startMin = minutesFromMidnight((startTime || '00:00').slice(0, 5));
  const top = ((startMin - gridStartMin) / 60) * HOUR_HEIGHT;
  const height = Math.max((durationMinutes / 60) * HOUR_HEIGHT, 24);
  const widthPct = 100 / Math.max(1, laneCount);
  return {
    top: `${top}px`,
    height: `${height}px`,
    left: `calc(${lane * widthPct}% + 2px)`,
    width: `calc(${widthPct}% - 4px)`,
    right: 'auto',
  };
}

export default function PublicSchedulePage() {
  const { token } = useParams();
  const [viewMode, setViewMode] = useState('template');
  const [weekStart, setWeekStart] = useState(() => mondayOf(moscowDateISO()));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewerTz, setViewerTz] = useState(() => loadViewerTzPreference());
  const now = useScheduleNowTick();
  const today = moscowDateISO(now);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/scheduler/public/${token}/`, {
          params: { week: weekStart },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Ссылка недействительна');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, weekStart]);

  const workStart = data?.work_start || '11:00';
  const workEnd = data?.work_end || '21:00';
  const gridSegments = useMemo(() => {
    const segs = [];
    for (const slot of data?.weekly_pattern || []) {
      segs.push({ start: slot.start_time, duration: slot.duration_minutes });
      for (const s of slot.members || []) {
        if (!s.start_time) continue;
        segs.push({
          start: s.start_time,
          duration: s.duration_minutes || slot.duration_minutes || 60,
        });
      }
    }
    for (const occ of data?.occurrences || []) {
      segs.push({
        start: timeFromOccurrenceStart(occ.start),
        duration: occ.duration_minutes,
      });
      for (const s of occ.members || []) {
        if (!s.start_time) continue;
        segs.push({
          start: s.start_time,
          duration: s.duration_minutes || occ.duration_minutes || 60,
        });
      }
    }
    return segs;
  }, [data]);
  const { gridStartMin, gridHours } = useMemo(
    () => scheduleGridExtent(workStart, workEnd, gridSegments),
    [workStart, workEnd, gridSegments]
  );
  const days = useMemo(() => weekDates(weekStart), [weekStart]);

  const occByDay = useMemo(() => {
    const map = Object.fromEntries(days.map((d) => [d, []]));
    for (const occ of data?.occurrences || []) {
      if (occ.is_cancelled) continue;
      const d = occurrenceDateMoscow(occ.start);
      if (!map[d]) continue;
      const startLabel = timeFromOccurrenceStart(occ.start);
      const dur = occ.duration_minutes || 60;
      const startMin = minutesFromMidnight(startLabel);
      map[d].push({
        ...occ,
        startLabel,
        dur,
        startMin,
        endMin: startMin + dur,
        id: occ.key,
      });
    }
    for (const d of days) {
      map[d] = assignLanes(map[d] || []);
    }
    return map;
  }, [data, days]);

  const templateByWeekday = useMemo(() => {
    const map = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    for (const slot of data?.weekly_pattern || []) {
      map[slot.weekday]?.push(slot);
    }
    for (const wd of Object.keys(map)) {
      const items = (map[wd] || []).map((slot) => {
        const startLabel = (slot.start_time || '00:00').slice(0, 5);
        const dur = slot.duration_minutes || 60;
        const startMin = minutesFromMidnight(startLabel);
        return {
          ...slot,
          startLabel,
          dur,
          startMin,
          endMin: startMin + dur,
          id: slot.id,
        };
      });
      map[wd] = assignLanes(items);
    }
    return map;
  }, [data]);

  const hourLabels = Array.from({ length: gridHours + 1 }, (_, i) => {
    const mins = gridStartMin + i * 60;
    const h = Math.floor(mins / 60);
    return `${String(h).padStart(2, '0')}:00`;
  });

  const todayWd = moscowWeekday(today);
  const nowLineTop = useMemo(
    () =>
      nowLineTopPx({
        now,
        workStart,
        workEnd,
        hourHeight: HOUR_HEIGHT,
        todayIso: today,
      }),
    [now, workStart, workEnd, today],
  );

  function cellStyle(startHHMM, duration, lane = 0, laneCount = 1) {
    return laneCellStyle(startHHMM, duration, gridStartMin, lane, laneCount);
  }

  return (
    <div className="sched-page sched-page--public">
      <div className="page-header">
        <h1>Занятость преподавателя</h1>
        <p className="page-subtitle">
          Без имён. Зелёный — группа, синий — индивидуально. <strong>Расписание</strong> — постоянные занятия; <strong>календарь</strong> — выбранная неделя.
        </p>
      </div>

      <div className="sched-mode-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`sched-mode-tab${viewMode === 'template' ? ' is-active' : ''}`}
          onClick={() => setViewMode('template')}
        >
          Расписание
        </button>
        <button
          type="button"
          role="tab"
          className={`sched-mode-tab${viewMode === 'calendar' ? ' is-active' : ''}`}
          onClick={() => setViewMode('calendar')}
        >
          Календарь недели
        </button>
      </div>

      <div className="sched-toolbar">
        {viewMode === 'calendar' ? (
          <div className="sched-week-nav">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setWeekStart(addDaysISO(weekStart, -7))}
            >
              ← Неделя
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setWeekStart(mondayOf(moscowDateISO()))}
            >
              Сегодня
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setWeekStart(addDaysISO(weekStart, 7))}
            >
              Неделя →
            </button>
            <span className="sched-week-label">
              {formatDateShort(weekStart)} — {formatDateShort(addDaysISO(weekStart, 6))}
            </span>
          </div>
        ) : (
          <p className="sched-toolbar-note muted" style={{ margin: 0 }}>
            Постоянные слоты по московскому времени
          </p>
        )}
        <ScheduleViewerClock
          showTimezoneSelect
          viewerTz={viewerTz}
          onViewerTzChange={setViewerTz}
          now={now}
        />
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p>Загрузка…</p>}

      {!loading && data && (
        <>
          <div className="sched-grid-wrap">
            <div className="sched-grid" style={{ '--sched-hour-h': `${HOUR_HEIGHT}px` }}>
              <div className="sched-grid-corner" />
              {(viewMode === 'template' ? WEEKDAY_NAMES : days).map((label, i) => {
                const isToday = viewMode === 'calendar' ? days[i] === today : i === todayWd;
                return (
                  <div key={label} className={`sched-day-head${isToday ? ' sched-day-head--today' : ''}`}>
                    <div className="sched-day-name">
                      {viewMode === 'template' ? label : WEEKDAY_NAMES[i]}
                    </div>
                    <div className="sched-day-date">
                      {viewMode === 'template' ? 'каждую неделю' : formatDateShort(days[i])}
                    </div>
                  </div>
                );
              })}
              <div className="sched-hours">
                {hourLabels.map((label) => (
                  <div key={label} className="sched-hour-label" style={{ height: HOUR_HEIGHT }}>
                    {label}
                  </div>
                ))}
              </div>
              {(viewMode === 'template' ? [0, 1, 2, 3, 4, 5, 6] : days).map((colKey, idx) => {
                const items =
                  viewMode === 'template'
                    ? templateByWeekday[colKey] || []
                    : occByDay[colKey] || [];
                const isToday = viewMode === 'calendar' ? colKey === today : colKey === todayWd;
                return (
                  <div
                    key={String(colKey) + idx}
                    className={`sched-day-col${isToday ? ' sched-day-col--today' : ''}`}
                    style={{ height: gridHours * HOUR_HEIGHT }}
                  >
                    {hourLabels.slice(0, -1).map((label) => (
                      <div key={label} className="sched-hour-line" style={{ height: HOUR_HEIGHT }} />
                    ))}
                    {isToday && <ScheduleNowLine topPx={nowLineTop} />}
                    {viewMode === 'template'
                      ? items.map((slot) => {
                          const start = slot.startLabel || (slot.start_time || '').slice(0, 5);
                          const label = slot.label || (slot.is_group ? 'группа' : 'занято');
                          return (
                            <div
                              key={slot.id}
                              className={`sched-occ sched-occ--public${
                                slot.is_group ? ' sched-occ--group' : ''
                              }`}
                              style={cellStyle(
                                start,
                                slot.dur || slot.duration_minutes,
                                slot.lane || 0,
                                slot.laneCount || 1
                              )}
                              title={`${label} · ${start} · ${slot.duration_minutes || 60} мин`}
                            >
                              <span className="sched-occ-meta">
                                <span className="sched-occ-time">
                                  {formatDualTimeFromTemplate(
                                    start,
                                    slot.weekday ?? colKey,
                                    viewerTz
                                  )}
                                </span>
                              </span>
                              <span className="sched-occ-people">
                                <span className="sched-occ-person">{label}</span>
                              </span>
                            </div>
                          );
                        })
                      : items.map((occ) => {
                          const start = occ.startLabel || timeFromOccurrenceStart(occ.start);
                          const label = occ.label || (occ.is_group ? 'группа' : 'занято');
                          const kindClass =
                            occ.kind === 'rescheduled'
                              ? ' sched-occ--moved'
                              : occ.kind === 'oneoff'
                                ? ' sched-occ--oneoff'
                                : '';
                          return (
                            <div
                              key={occ.key}
                              className={`sched-occ sched-occ--public${
                                occ.is_group ? ' sched-occ--group' : ''
                              }${kindClass}`}
                              style={cellStyle(
                                start,
                                occ.dur || occ.duration_minutes,
                                occ.lane || 0,
                                occ.laneCount || 1
                              )}
                              title={`${label} · ${start} · ${occ.duration_minutes || 60} мин`}
                            >
                              <span className="sched-occ-meta">
                                <span className="sched-occ-time">
                                  {formatDualTime(occ.start, viewerTz)}
                                </span>
                              </span>
                              <span className="sched-occ-people">
                                <span className="sched-occ-person">{label}</span>
                              </span>
                            </div>
                          );
                        })}
                  </div>
                );
              })}
            </div>
          </div>

          {viewMode === 'template' && (
            <section className="sched-panel">
              <h2>Свободное время (по шаблону)</h2>
              <pre className="sched-free-text">{data.free_text}</pre>
              <p className="muted" style={{ marginTop: '0.75rem' }}>
                Разовая отмена на одной неделе не делает слот свободным «навсегда» — в шаблоне он остаётся занятым.
              </p>
            </section>
          )}

          {viewMode === 'calendar' && (
            <p className="sched-legend muted">
              Только занятия, которые состоятся. Зелёный — группа, синий — индивидуально.
              Отменённые в календарь не попадают; в шаблоне занятие остаётся.
            </p>
          )}
        </>
      )}
    </div>
  );
}
